import {useEffect, useRef} from 'react';
import {useInView} from 'react-intersection-observer';
import {useRouter} from 'next/router';

import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useIdentity from '@/hooks/useIdentity';

export const PreciseAddressNudgeVariation = {
  Control: 'control',
  Fallback: 'fallback',
  RedDot: 'treatment_red_dot',
  TextPrompt: 'treatment_text_prompt',
} as const;

// Exposure only fires on these, so keep this list in sync with those getStringAssignment calls.
export const PRECISE_ADDRESS_NUDGE_PATHNAMES: ReadonlySet<string> = new Set([
  '/',
  '/catering/search/[orderId]',
  '/catering/[catererUrlId]/[[...date]]',
]);

type UsePreciseAddressNudgeOptions = {
  trackExposure?: boolean;
};

// ezx578::ARC::PreciseAddressNudge — arm gate plus opt-in exposure.

const usePreciseAddressNudge = ({trackExposure = false}: UsePreciseAddressNudgeOptions = {}) => {
  const {variation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.PreciseAddressNudge,
  );
  const {pathname} = useRouter();
  // setup the experiment only on specific pages.
  const isOnExperimentPage = PRECISE_ADDRESS_NUDGE_PATHNAMES.has(pathname);
  // targets visitors only, so signed-in users never see a treatment or report exposure.
  const {isLoggedIn, loading: isIdentityLoading} = useIdentity();
  const isEligible = isOnExperimentPage && !isIdentityLoading && !isLoggedIn;

  const {ref: seenRef, inView} = useInView({threshold: 0, triggerOnce: true});
  const hasTrackedExposure = useRef(false);

  useEffect(() => {
    if (!trackExposure || !isEligible || hasTrackedExposure.current) return;
    if (!inView || variation === PreciseAddressNudgeVariation.Fallback) return;

    hasTrackedExposure.current = true;
    trackExperimentExposed();
  }, [trackExposure, isEligible, inView, variation, trackExperimentExposed]);

  if (!isEligible) {
    return {
      seenRef,
      showRedDot: false,
      showTextPrompt: false,
      variation: PreciseAddressNudgeVariation.Fallback,
    };
  }

  return {
    seenRef,
    showRedDot: variation === PreciseAddressNudgeVariation.RedDot,
    showTextPrompt: variation === PreciseAddressNudgeVariation.TextPrompt,
    variation,
  };
};

export default usePreciseAddressNudge;
